import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Box, Button, CircularProgress, Link as MuiLink } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import theme from 'theme'
import { mapMessagesForDownload } from 'lib/messagesLib'
import { ASSIGNMENT_STATE_BLIND_MINUS } from 'lib/assignmentStates'
import { getCarContextKey, getSessionIdFromCar } from 'lib/carContextKey'
import { getParentSessionId, isRepairSession } from 'lib/sessionLifecycle'

import ConfirmBar from 'components/atoms/ConfirmBar'
import SearchField from 'components/atoms/SearchField'
import CarsList from 'components/atoms/CarsList'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarCard from 'components/atoms/CarCard'
import AppealingIndex from 'components/atoms/AppealingIndex'
import DownloadBar from 'components/atoms/DownloadBar'
import LogIcon from 'components/atoms/LogIcon'

import useCarsFilters from 'hooks/useCarsFilters'
import useLogs from 'hooks/useLogs'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useInputOutput from 'hooks/useInputOutput'
import useSession from 'hooks/useSession'

import {
  getCarFinalPrice,
  getAssignmentChoiceTotalsForCar,
  getAssignmentChoiceTotalsByPlate,
  getAssignmentState,
  getAutoAssignmentDisplayState,
  getAssignmentPhaseLabel,
  getBlindOriginLabel,
} from '../Assignment/assignmentLib'
import useAssignmentSession from '../Assignment/useAssignmentSession'
import useCarsToAssign from '../Assignment/useCarsToAssign'
import useSnackbar from '../Snackbar/useSnackbar'
import AssignmentCarAdditionalDetails from '../Assignment/AssignmentCarAdditionalDetails'
import AssignmentCarDetails from '../AssignmentCarDetails'
import AssignmentConfirmationDialog from '../Assignment/AssignmentConfirmationDialog'

import AssignmentChoicesRecap from './AssignmentChoicesRecap'

const normalizeList = (list = []) => {
  if (Array.isArray(list)) {
    return list.map((item) => String(item))
  }

  if (typeof list === 'string') {
    try {
      const parsed = JSON.parse(list)
      return Array.isArray(parsed) ? parsed.map((item) => String(item)) : []
    } catch (_) {
      return []
    }
  }

  return []
}

const listIncludesPartner = (list = [], partnerId) =>
  normalizeList(list).includes(String(partnerId))

const isPresentValue = (value) =>
  value !== null && value !== undefined && value !== ''

const normalizeBlindOfferPrices = (blindOfferPrices) => {
  if (!blindOfferPrices) {
    return {}
  }

  if (typeof blindOfferPrices === 'string') {
    try {
      const parsed = JSON.parse(blindOfferPrices)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (_) {
      return {}
    }
  }

  return typeof blindOfferPrices === 'object' ? blindOfferPrices : {}
}

const getBlindPartnerCellValue = (car, partnerId) => {
  const normalizedPartnerId = String(partnerId)
  const blindOfferPrices = normalizeBlindOfferPrices(car?.blindOfferPrices)
  const blindPreferenceFromBlindList = listIncludesPartner(
    car?.blindFirstChoiceList,
    normalizedPartnerId
  )
  const blindPreferenceFallback =
    car?.assignmentType === 'blind' &&
    listIncludesPartner(car?.firstChoiceList, normalizedPartnerId)

  const hasBlindPreference =
    blindPreferenceFromBlindList || blindPreferenceFallback

  const blindOfferPriceFromMap = blindOfferPrices[normalizedPartnerId]
  const blindOfferPriceFallback =
    car?.assignmentType === 'blind' &&
    String(car?.assignedPartnerId) === normalizedPartnerId
      ? car?.blindFinalPrice
      : null

  const blindOfferPrice = isPresentValue(blindOfferPriceFromMap)
    ? blindOfferPriceFromMap
    : blindOfferPriceFallback

  if (isPresentValue(blindOfferPrice)) {
    return `${blindOfferPrice}`
  }

  if (hasBlindPreference) {
    return '1'
  }

  return null
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function buildCarRow(
  car,
  t,
  session,
  partners,
  assignmentChoiceTotalsByPlate = {},
  fallbackSessionId = null
) {
  const autoAssignmentDisplayState = getAutoAssignmentDisplayState(car)
  const hasFinalColumn =
    session.agenda.assignment.state === 'closed' ||
    car?.autoAssignmentPartnerId !== car?.assignedPartnerId

  const finalAssignment = hasFinalColumn
    ? t(
        `assignment.excel.status.${
          session.agenda.assignment.state
        }.${getAssignmentState(car)}`
      )
    : '-'
  const finalPrice = getCarFinalPrice(car)
  const assignmentPhaseLabel = getAssignmentPhaseLabel(t, car, session)
  const blindOriginLabel = getBlindOriginLabel(t, car)
  const totals = getAssignmentChoiceTotalsForCar(
    assignmentChoiceTotalsByPlate,
    car,
    fallbackSessionId
  )
  const firstChoiceCount = Number(totals?.firstChoice) || 0
  const blindOffersCount = Number(totals?.blindOffers) || 0
  const classicFirstChoiceList =
    car?.assignmentType === 'blind'
      ? car?.nonBlindFirstChoiceList || []
      : car?.firstChoiceList || []
  const classicSecondChoiceList =
    car?.assignmentType === 'blind'
      ? car?.nonBlindSecondChoiceList || []
      : car?.secondChoiceList || []

  return [
    car.plate,
    `${car.title}`,
    t(
      `assignment.excel.status.${session.agenda.assignment.state}.${autoAssignmentDisplayState}`
    ),
    finalAssignment,
    finalPrice ?? '-',
    (car.autoAssignmentPartnerId &&
      partners.find(
        (partner) => partner.partnerId === car.autoAssignmentPartnerId
      )?.name) ||
      '-',
    (car.assignedPartnerId &&
      hasFinalColumn &&
      partners.find((partner) => partner.partnerId === car.assignedPartnerId)
        ?.name) ||
      '-',
    `${firstChoiceCount}`,
    `${blindOffersCount}`,
    ...partners.map((partner) => {
      const blindCellValue = getBlindPartnerCellValue(car, partner.partnerId)

      if (blindCellValue !== null) {
        return blindCellValue
      }

      if (listIncludesPartner(classicFirstChoiceList, partner.partnerId)) {
        return '1'
      }

      if (listIncludesPartner(classicSecondChoiceList, partner.partnerId)) {
        return '2'
      }

      return '-'
    }),
    assignmentPhaseLabel,
    blindOriginLabel,
    car.messages ? mapMessagesForDownload(car.messages, t, true) : '-',
  ]
}

function getRows(
  titlesRow,
  cars,
  t,
  session,
  partners,
  assignmentChoiceTotalsByPlate = {},
  fallbackSessionId = null
) {
  return [
    titlesRow,
    ...cars.map((car) =>
      buildCarRow(
        car,
        t,
        session,
        partners,
        assignmentChoiceTotalsByPlate,
        fallbackSessionId
      )
    ),
  ]
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function AssignmentCarAssignment({
  session,
  partners,
  cars,
  handleValidation,
  handleConfirm,
  dateValue,
  setDateValue,
  readOnly,
  contentIsReadOnly,
  refetchList,
  setShowHeader,
}) {
  const { t } = useTranslation()
  const repairSession = isRepairSession(session)
  const {
    isUpdating: isUpdatingBlindConfirmation,
    blindAssignmentEndedAt: confirmedBlindAssignmentEndedAt,
    confirmAssignment: confirmBlindAssignment,
  } = useAssignmentSession({ isBlind: true })

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()
  const { getSession } = useSession()
  const { getCarsToAssign } = useCarsToAssign()
  const { showSnackbarMessage } = useSnackbar()

  const [showDetails, setShowDetails] = useState()
  const [filteredCars, setFilteredCars] = useState([])

  useEffect(() => {
    setShowHeader(!showDetails)
  }, [setShowHeader, showDetails])

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setDamageRange,
    setYearRange,
    setBrands,
    setModels,
    setLocations,
    setFuels,
    setCategory,
    setQuery,
    setAssignmentCaps: setFilteringCaps,
    setAssignmentType,
    setAssignmentPhase,
    setAssignmentPartners,
    filterCars,
    resetFilters,
  } = useCarsFilters({
    startDataFilters: {
      assignmentPartners: [
        { label: t('common.form.all'), value: '' },
        ...partners.map((partner) => ({
          label: partner.name,
          value: partner.partnerId,
        })),
      ],
    },
  })

  const { saveXls } = useInputOutput()

  useEffect(() => {
    if (!cars.length) {
      return
    }

    initFilters(cars)
  }, [cars, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars, session) =>
      setFilteredCars(filterCars(cars, filters, session)),
    100
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars, session)
  }, [
    cars,
    handleSetFilteredCars,
    filters,
    setFilteredCars,
    filterCars,
    session,
  ])

  const assignmentChoiceTotalsByPlate = useMemo(
    () => getAssignmentChoiceTotalsByPlate(cars),
    [cars]
  )
  const blindNegativeCarsToReview = useMemo(
    () =>
      cars.filter(
        (car) =>
          getAutoAssignmentDisplayState(car) === ASSIGNMENT_STATE_BLIND_MINUS
      ).length,
    [cars]
  )
  const isBlindAssignmentConfirmed = Boolean(
    session?.blindAssignmentEndedAt || confirmedBlindAssignmentEndedAt
  )
  const hasBlindNegativeCarsToReview = blindNegativeCarsToReview > 0
  const shouldShowBlindConfirmationButton = Boolean(
    hasBlindNegativeCarsToReview &&
      (session?.blindAssignmentStartedAt || isBlindAssignmentConfirmed)
  )
  const isBlindConfirmationRequired =
    hasBlindNegativeCarsToReview &&
    Boolean(session?.blindAssignmentStartedAt) &&
    !isBlindAssignmentConfirmed
  const isBlindConfirmationDisabled =
    !session?.sessionId ||
    !!session?.assignmentEndedAt ||
    isBlindAssignmentConfirmed ||
    !session?.blindAssignmentStartedAt ||
    readOnly ||
    contentIsReadOnly ||
    isUpdatingBlindConfirmation
  const shouldAutoConfirmEmptyBlindAssignment =
    Boolean(session?.blindAssignmentStartedAt) &&
    !isBlindAssignmentConfirmed &&
    !hasBlindNegativeCarsToReview

  const handleConfirmAssignment = useCallback(async () => {
    if (shouldAutoConfirmEmptyBlindAssignment) {
      const confirmed = await confirmBlindAssignment(session?.sessionId)

      if (confirmed === false) {
        return false
      }
    }

    return handleConfirm()
  }, [
    confirmBlindAssignment,
    handleConfirm,
    session?.sessionId,
    shouldAutoConfirmEmptyBlindAssignment,
  ])

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleConfirmAssignment)

  const enrichedFilteredCars = useMemo(
    () =>
      filteredCars.map((car) => {
        const totals = getAssignmentChoiceTotalsForCar(
          assignmentChoiceTotalsByPlate,
          car,
          session?.sessionId
        )
        return {
          ...car,
          firstChoicePartnersCount: totals.firstChoice ?? 0,
          blindOfferPartnersCount: totals.blindOffers ?? 0,
        }
      }),
    [assignmentChoiceTotalsByPlate, filteredCars, session?.sessionId]
  )

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.plate'),
      t('common.excel.name'),
      t('assignment.excel.originalStatus'),
      t(`assignment.excel.finalStatus.${session.agenda.assignment.state}`),
      t('common.excel.finalPrice'),
      t('assignment.excel.originalPartner'),
      t(`assignment.excel.finalPartner.${session.agenda.assignment.state}`),
      t('common.excel.firstChoice'),
      t('common.excel.blindOffers'),
      ...partners.map((partner) => partner.name),
      t('assignment.metadata.phaseTitle'),
      t('assignment.metadata.blindOriginTitle'),
      t('common.excel.messages'),
    ]

    const rows = getRows(
      titlesRow,
      cars,
      t,
      session,
      partners,
      assignmentChoiceTotalsByPlate,
      session?.sessionId
    )

    const parentSessionId = repairSession ? getParentSessionId(session) : null

    if (parentSessionId) {
      try {
        const [motherSession, motherCars] = await Promise.all([
          getSession(parentSessionId),
          getCarsToAssign(parentSessionId),
        ])

        const repairPlates = new Set(cars.map((car) => car.plate))
        const motherOnlyCars = motherCars.filter(
          (car) => !repairPlates.has(car.plate)
        )

        if (motherOnlyCars.length) {
          const motherTotalsByPlate =
            getAssignmentChoiceTotalsByPlate(motherOnlyCars)

          rows.push(
            ...motherOnlyCars.map((car) =>
              buildCarRow(
                car,
                t,
                motherSession,
                partners,
                motherTotalsByPlate,
                parentSessionId
              )
            )
          )
        }
      } catch (e) {
        showSnackbarMessage({
          severity: 'error',
          message: t('common.error.fetchData', {
            attribute: e?.response?.status,
          }),
        })
      }
    }

    saveXls('car-assignment', rows)
  }

  const handleOpenDetails = (car) =>
    setShowDetails({
      plate: car.plate,
      assignmentType: car.assignmentType || 'nonBlind',
      sessionId: (getSessionIdFromCar(car) ?? session?.sessionId) || null,
      contextKey: getCarContextKey(car, session?.sessionId),
    })

  return showDetails ? (
    <AssignmentCarDetails
      plate={showDetails?.plate}
      assignmentType={showDetails?.assignmentType}
      detailsSessionId={showDetails?.sessionId}
      detailsContextKey={showDetails?.contextKey}
      session={session}
      cars={enrichedFilteredCars}
      setShowDetails={setShowDetails}
      refetchList={refetchList}
      contentIsReadOnly={contentIsReadOnly}
    />
  ) : (
    <>
      <Box mt={1} data-testid="assignment-car" />
      <ConfirmBar
        label={t(
          repairSession
            ? 'assignment.car-assignment.repair.confirmButton'
            : 'assignment.car-assignment.confirmButton'
        )}
        left={
          <SearchField
            data-testid="text-search"
            placeholder={t('common.form.searchBy')}
            value={filters.query}
            onChange={(event) => setQuery(event.target.value)}
          />
        }
        leftProps={{ sx: { width: 0.5 } }}
        right={
          <>
            <Collapse orientation="horizontal" in={isFetchingLogs}>
              <div style={{ marginLeft: 4, marginRight: 4 }}>
                <CircularProgress size={20} />
              </div>
            </Collapse>
            <LogIcon
              disabled={isFetchingLogs}
              handleClick={() =>
                saveLogs(`logs_assignment_${session?.sessionId}`, {
                  area: t('common.logs.assignment'),
                  sessionId: session?.sessionId,
                })
              }
            />
            <DownloadBar handleDownload={handleDownload} />
            {shouldShowBlindConfirmationButton && (
              <Button
                data-testid="confirm-blind-negative-button"
                color="primary"
                variant="outlined"
                disabled={isBlindConfirmationDisabled}
                onClick={() => confirmBlindAssignment(session?.sessionId)}
                sx={{
                  marginLeft: (theme) => theme.spacing(2),
                  minWidth: 0.275,
                }}
              >
                {t('assignment.car-assignment.confirmBlindNegativeButton', {
                  count: blindNegativeCarsToReview,
                  defaultValue: `Conferma Blind- (${blindNegativeCarsToReview})`,
                })}
              </Button>
            )}
          </>
        }
        top={
          <AssignmentChoicesRecap
            cars={cars}
            filters={filters}
            filtersData={filtersData}
            setAssignmentPartners={setAssignmentPartners}
            setFilteringCaps={setFilteringCaps}
            setAssignmentType={setAssignmentType}
            showAppealing={false}
          />
        }
        topProps={{
          sx: {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
        disabled={
          !handleValidation() ||
          isBlindConfirmationRequired ||
          !!session?.assignmentEndedAt ||
          readOnly ||
          contentIsReadOnly
        }
        handleConfirm={handleOpenConfirmationDialog}
        sticky
      />
      <CarsList
        left={
          <CarFiltersColumn
            filters={filters}
            filtersData={filtersData}
            filteredCars={
              filteredCars?.length !== cars.length ? filteredCars : undefined
            }
            setKmRange={setKmRange}
            setDamageRange={setDamageRange}
            setYearRange={setYearRange}
            setBrands={setBrands}
            setModels={setModels}
            setLocations={setLocations}
            setFuels={setFuels}
            setCategory={setCategory}
            setAssignmentPhase={setAssignmentPhase}
            resetFilters={resetFilters}
            readOnly={readOnly}
          />
        }
        right={
          <>
            {enrichedFilteredCars.map((car, index) => (
              <CarCard
                data-testid={`car-${index}`}
                key={`${car.assignmentType || 'nonBlind'}-${car.plate}`}
                onClick={() => handleOpenDetails(car)}
                height={theme.custom.carCardAdditionalMaxHeight}
                car={car}
                right={
                  <>
                    <AppealingIndex value={car.appealing} />
                    <MuiLink
                      color="primary"
                      data-testid="car-detail-right-click"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleOpenDetails(car)}
                      component={Box}
                      variant="body2"
                    >
                      <strong>{t('carsToBuy.details')}</strong>
                    </MuiLink>
                  </>
                }
                bottom={
                  <AssignmentCarAdditionalDetails
                    partners={partners.map((partner) => ({
                      partnerId: partner.partnerId,
                      partnerName: partner.name,
                    }))}
                    car={car}
                    sessionState={session.agenda.assignment.state}
                    session={session}
                  />
                }
              />
            ))}
          </>
        }
      />
      <AssignmentConfirmationDialog
        session={session}
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
        dateValue={dateValue}
        setDateValue={setDateValue}
      />
    </>
  )
}

AssignmentCarAssignment.propTypes = {
  session: T.object.isRequired,
  partners: T.array,
  cars: T.array,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func,
  dateValue: T.instanceOf(Date),
  setDateValue: T.func,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
